import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, MapPin, Download, Edit3, Upload, X,
  Loader2, CheckCircle2, XCircle, Circle, FileText, Camera, Image as ImageIcon,
  AlertCircle, Save, RefreshCw, ExternalLink, Calendar, FileDown,
  HandCoins, PlayCircle, StopCircle, Search as SearchIcon, Scissors, Filter as FilterIcon,
  Share2, Users, Copy, KeyRound, LogIn, UserPlus, Check, Crown, GitMerge,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  saveDeliveryListOffline, getDeliveryListOffline, getAllDeliveryListsOffline,
  enqueueDeliveryEdit, flushDeliveryQueue, getPendingQueueCount, computeCounts,
} from "@/utils/petaOfflineDB";
import { exportDeliveryPdf, exportDeliveryDocx, exportDeliveryPhotosPdf, exportDeliveryPhotosDocx } from "@/utils/deliveryReport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- helpers ----------
const parseIds = (raw) => {
  if (!raw) return [];
  // Split on newline, comma, semicolon, tab, or whitespace
  const tokens = String(raw).split(/[\s,;]+/);
  const seen = new Set();
  const out = [];
  for (const t of tokens) {
    const cid = t.trim();
    if (!cid) continue;
    if (seen.has(cid)) continue;
    seen.add(cid);
    out.push(cid);
  }
  return out;
};

// Upload a PDF / Excel / CSV / TXT and let the backend extract the customer IDs
// (No.Samb, 8 digits). Returns a deduped array of id strings.
const extractIdsFromFile = async (file, authToken) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await axios.post(`${API}/delivery-lists/extract-ids`, fd, {
    headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
  });
  return res.data?.ids || [];
};

const compressImage = (file, maxWidth = 800, quality = 0.75) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const STATUS_META = {
  pending: { label: "Belum", emoji: "🔴", color: "bg-rose-100 text-rose-700 border-rose-200" },
  delivered: { label: "Sudah", emoji: "🟢", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  not_found: { label: "Tidak Ketemu", emoji: "⚫", color: "bg-slate-200 text-slate-600 border-slate-300" },
  promised_pay: { label: "Janji Bayar", emoji: "🔵", color: "bg-sky-100 text-sky-700 border-sky-200" },
  lunas: { label: "Lunas", emoji: "💰", color: "bg-teal-100 text-teal-700 border-teal-300" },
  putus: { label: "Putus", emoji: "✂️", color: "bg-purple-100 text-purple-700 border-purple-200" },
};

// Statuses that carry a "tanggal ditetapkan" date (like promised_pay has its own date).
const DATED_STATUSES = ["delivered", "not_found", "lunas", "putus"];

// Normalise legacy single `photo` + new `photos[]` into one array (max 5).
const itemPhotos = (it) => {
  const arr = Array.isArray(it?.photos) ? it.photos.filter(Boolean) : [];
  if (arr.length) return arr.slice(0, 5);
  return it?.photo ? [it.photo] : [];
};

// ============ INDEX PAGE ============
export default function AntarSurat() {
  const { id } = useParams();
  return id ? <DeliveryListDetail listId={id} /> : <DeliveryListIndex />;
}

function DeliveryListIndex() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [shareList, setShareList] = useState(null);
  const [mergeList, setMergeList] = useState(null);
  const authToken = localStorage.getItem("user_token") || localStorage.getItem("admin_token");
  const userRole = (localStorage.getItem("user_role") || "").toLowerCase() || (localStorage.getItem("admin_token") ? "admin" : "");

  const isAllowed = useMemo(() => ["petugas", "kasir", "admin"].includes(userRole) && !!authToken, [userRole, authToken]);

  const fetchLists = useCallback(async () => {
    if (!isAllowed) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API}/delivery-lists`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setLists(res.data?.lists || []);
    } catch (e) {
      // Offline / network error → show cached lists if any
      const cached = await getAllDeliveryListsOffline();
      if (cached.length > 0) {
        setLists(cached.map((l) => ({ id: l.id, name: l.name, created_at: l.created_at, counts: l.counts })));
        toast.info("Mode offline: menampilkan daftar tersimpan.");
      } else {
        toast.error(e?.response?.data?.detail || "Gagal memuat daftar.");
      }
    } finally {
      setLoading(false);
    }
  }, [isAllowed, authToken]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const handleDelete = async (lid, name) => {
    if (!window.confirm(`Hapus daftar "${name}"? Riwayat status & foto akan hilang.`)) return;
    try {
      await axios.delete(`${API}/delivery-lists/${lid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Daftar dihapus.");
      fetchLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    }
  };

  const handleLeave = async (lid, name) => {
    if (!window.confirm(`Keluar dari daftar berbagi "${name}"? Anda tak lagi bisa mengaksesnya.`)) return;
    try {
      await axios.delete(`${API}/delivery-lists/${lid}/members/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success("Anda keluar dari daftar.");
      fetchLists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal keluar.");
    }
  };

  if (!isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-xl font-bold mb-2">Akses Ditolak</h2>
          <p className="text-sm text-slate-600 mb-4">Fitur Antar Surat khusus untuk petugas, kasir, dan admin.</p>
          <Button onClick={() => navigate("/")} variant="outline" data-testid="back-home-btn">Kembali</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-emerald-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
            data-testid="antar-surat-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-base flex items-center gap-2 text-emerald-700 truncate">
              <FileText className="w-5 h-5" /> Antar Surat Tunggakan
            </h1>
            <p className="text-[11px] text-slate-500 truncate">Daftar pelanggan untuk antar surat — disinkronkan ke akun {userRole}</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            onClick={() => setShowJoin(true)}
            data-testid="join-by-code-btn"
          >
            <LogIn className="w-4 h-4" /> Gabung Kode
          </Button>
          <Button
            size="sm"
            className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => setShowCreate(true)}
            data-testid="create-delivery-list-btn"
          >
            <Plus className="w-4 h-4" /> Daftar Baru
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Memuat daftar...
          </div>
        ) : lists.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <h3 className="font-semibold text-slate-700 mb-1">Belum ada daftar antar surat</h3>
            <p className="text-sm text-slate-500 mb-4">Buat daftar baru untuk mulai antar surat tunggakan.</p>
            <Button
              className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setShowCreate(true)}
              data-testid="create-first-list-btn"
            >
              <Plus className="w-4 h-4" /> Buat Daftar Pertama
            </Button>
          </div>
        ) : (
          lists.map((l) => (
            <div
              key={l.id}
              className="bg-white rounded-xl shadow-sm border border-slate-200 hover:border-emerald-300 hover:shadow-md transition p-4"
              data-testid={`delivery-list-card-${l.id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-base text-slate-800 truncate" data-testid={`list-name-${l.id}`}>{l.name}</h3>
                  <p className="text-[11px] text-slate-500">
                    Dibuat: {new Date(l.created_at).toLocaleString("id-ID")}
                  </p>
                  {(l.is_shared || !l.is_owner) && (
                    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`share-badge-${l.id}`}>
                      {l.is_owner ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          <Crown className="w-3 h-3" /> Pemilik · {l.member_count} anggota
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200">
                          <Users className="w-3 h-3" /> Dibagikan oleh {l.owner_name || "pemilik"}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-emerald-600 leading-none">{l.counts.total}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wide">total ID</div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="flex h-2 rounded-full overflow-hidden bg-slate-100 mb-2">
                {l.counts.delivered > 0 && (
                  <div
                    className="bg-emerald-500"
                    style={{ width: `${(l.counts.delivered / l.counts.total) * 100}%` }}
                    title={`Sudah: ${l.counts.delivered}`}
                  />
                )}
                {(l.counts.promised_pay || 0) > 0 && (
                  <div
                    className="bg-sky-500"
                    style={{ width: `${((l.counts.promised_pay || 0) / l.counts.total) * 100}%` }}
                    title={`Janji bayar: ${l.counts.promised_pay || 0}`}
                  />
                )}
                {l.counts.not_found > 0 && (
                  <div
                    className="bg-slate-400"
                    style={{ width: `${(l.counts.not_found / l.counts.total) * 100}%` }}
                    title={`Tidak ketemu: ${l.counts.not_found}`}
                  />
                )}
              </div>

              <div className="flex flex-wrap gap-1.5 mb-3 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full border ${STATUS_META.pending.color}`}>
                  {STATUS_META.pending.emoji} Belum {l.counts.pending}
                </span>
                <span className={`px-2 py-0.5 rounded-full border ${STATUS_META.delivered.color}`}>
                  {STATUS_META.delivered.emoji} Sudah {l.counts.delivered}
                </span>
                {(l.counts.promised_pay || 0) > 0 && (
                  <span className={`px-2 py-0.5 rounded-full border ${STATUS_META.promised_pay.color}`}>
                    {STATUS_META.promised_pay.emoji} Janji {l.counts.promised_pay}
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded-full border ${STATUS_META.not_found.color}`}>
                  {STATUS_META.not_found.emoji} Tidak Ketemu {l.counts.not_found}
                </span>
                {(l.counts.putus || 0) > 0 && (
                  <span className={`px-2 py-0.5 rounded-full border ${STATUS_META.putus.color}`}>
                    {STATUS_META.putus.emoji} Putus {l.counts.putus}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => navigate(`/antar-surat/${l.id}`)}
                  data-testid={`open-list-${l.id}-btn`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> Buka & Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  onClick={() => navigate(`/peta-global?antar_surat=${l.id}`)}
                  data-testid={`map-list-${l.id}-btn`}
                >
                  <MapPin className="w-3.5 h-3.5" /> Lihat di Peta
                </Button>
                {l.is_owner && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={() => setShareList(l)}
                    data-testid={`share-list-${l.id}-btn`}
                  >
                    <Share2 className="w-3.5 h-3.5" /> Bagikan
                  </Button>
                )}
                {l.is_owner && lists.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-teal-300 text-teal-700 hover:bg-teal-50"
                    onClick={() => setMergeList(l)}
                    data-testid={`merge-list-${l.id}-btn`}
                  >
                    <GitMerge className="w-3.5 h-3.5" /> Gabung
                  </Button>
                )}
                {l.is_owner ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50 ml-auto"
                    onClick={() => handleDelete(l.id, l.name)}
                    data-testid={`delete-list-${l.id}-btn`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Hapus
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-slate-300 text-slate-600 hover:bg-slate-50 ml-auto"
                    onClick={() => handleLeave(l.id, l.name)}
                    data-testid={`leave-list-${l.id}-btn`}
                  >
                    <X className="w-3.5 h-3.5" /> Keluar
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreate && (
        <CreateListModal
          authToken={authToken}
          onClose={() => setShowCreate(false)}
          onCreated={(newList) => {
            setShowCreate(false);
            navigate(`/antar-surat/${newList.id}`);
          }}
        />
      )}

      {showJoin && (
        <JoinModal
          authToken={authToken}
          onClose={() => setShowJoin(false)}
          onJoined={() => { setShowJoin(false); fetchLists(); }}
        />
      )}

      {shareList && (
        <ShareModal
          authToken={authToken}
          list={shareList}
          onClose={() => setShareList(null)}
          onChanged={fetchLists}
        />
      )}

      {mergeList && (
        <MergeModal
          authToken={authToken}
          targetList={mergeList}
          allLists={lists}
          onClose={() => setMergeList(null)}
          onMerged={() => { setMergeList(null); fetchLists(); }}
        />
      )}
    </div>
  );
}

// ============ MERGE MODAL ============
function MergeModal({ authToken, targetList, allLists, onClose, onMerged }) {
  const [sourceId, setSourceId] = useState("");
  const [deleteSource, setDeleteSource] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const sources = useMemo(
    () => (allLists || []).filter((l) => l.id !== targetList.id),
    [allLists, targetList.id]
  );
  const selectedSource = sources.find((s) => s.id === sourceId) || null;

  useEffect(() => {
    if (selectedSource && !selectedSource.is_owner && deleteSource) setDeleteSource(false);
  }, [selectedSource, deleteSource]);

  const handleMerge = async () => {
    if (!sourceId) return toast.error("Pilih daftar sumber untuk digabung.");
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/delivery-lists/${targetList.id}/merge`,
        { source_list_id: sourceId, delete_source: deleteSource },
        { headers }
      );
      const { added = 0, updated = 0, source_deleted } = res.data || {};
      toast.success(`Berhasil digabung: ${added} ID baru, ${updated} ID diperbarui.${source_deleted ? " Daftar sumber dihapus." : ""}`);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menggabungkan daftar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="merge-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="font-bold text-base flex items-center gap-2 text-emerald-700">
            <GitMerge className="w-5 h-5" /> Gabung Daftar
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="merge-close-btn">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-600">
            Gabungkan ID dari daftar lain ke dalam <b>{targetList.name}</b>. ID yang sama tidak diduplikat — status dengan pembaruan terbaru yang dipertahankan.
          </p>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Pilih daftar sumber</label>
            {sources.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center border border-dashed border-slate-200 rounded-lg">Tidak ada daftar lain untuk digabung.</p>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {sources.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSourceId(s.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${sourceId === s.id ? "border-emerald-400 bg-emerald-50" : "border-slate-200 hover:border-emerald-200"}`}
                    data-testid={`merge-source-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm text-slate-800 truncate">{s.name}</span>
                      <span className="text-[11px] text-slate-500 shrink-0">{s.counts?.total || 0} ID</span>
                    </div>
                    {!s.is_owner && <span className="text-[10px] text-violet-600">Dibagikan</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedSource && (
            <label className={`flex items-center gap-2 text-xs ${selectedSource.is_owner ? "text-slate-700" : "text-slate-400"}`}>
              <input
                type="checkbox"
                checked={deleteSource}
                disabled={!selectedSource.is_owner}
                onChange={(e) => setDeleteSource(e.target.checked)}
                data-testid="merge-delete-source-checkbox"
              />
              Hapus daftar sumber setelah digabung
              {!selectedSource.is_owner && " (hanya untuk daftar milik Anda)"}
            </label>
          )}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="merge-cancel-btn">Batal</Button>
            <Button
              className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleMerge}
              disabled={busy || !sourceId}
              data-testid="merge-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />} Gabungkan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ SHARE MODAL ============
function ShareModal({ authToken, list, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [shareCode, setShareCode] = useState(list.share_code || null);
  const [availUsers, setAvailUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        axios.get(`${API}/delivery-lists/${list.id}/members`, { headers }),
        axios.get(`${API}/delivery-lists/share/available-users`, { headers }),
      ]);
      setMembers(m.data?.members || []);
      setShareCode(m.data?.share_code || null);
      setAvailUsers(a.data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data berbagi.");
    } finally { setLoading(false); }
  }, [headers, list.id]);

  useEffect(() => { load(); }, [load]);

  const memberIds = new Set(members.map((m) => m.id));
  const addable = availUsers.filter((u) => !memberIds.has(u.id));

  const genCode = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/delivery-lists/${list.id}/share-code`, {}, { headers });
      setShareCode(res.data.share_code);
      toast.success("Kode berbagi dibuat.");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat kode."); }
    finally { setBusy(false); }
  };

  const disableCode = async () => {
    if (!window.confirm("Nonaktifkan kode berbagi? Anggota yang sudah bergabung tetap punya akses.")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/delivery-lists/${list.id}/share-code`, { headers });
      setShareCode(null);
      toast.success("Kode dinonaktifkan.");
      onChanged?.();
    } catch (e) { toast.error("Gagal menonaktifkan."); }
    finally { setBusy(false); }
  };

  const copyCode = async () => {
    if (!shareCode) return;
    try { await navigator.clipboard.writeText(shareCode); toast.success("Kode disalin."); }
    catch { toast.info(`Kode: ${shareCode}`); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const addMembers = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      await axios.post(`${API}/delivery-lists/${list.id}/members`, { user_ids: selected }, { headers });
      setSelected([]);
      toast.success("Anggota ditambahkan.");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah anggota."); }
    finally { setBusy(false); }
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Keluarkan ${name} dari daftar ini?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/delivery-lists/${list.id}/members/${uid}`, { headers });
      toast.success("Anggota dikeluarkan.");
      await load();
      onChanged?.();
    } catch (e) { toast.error("Gagal mengeluarkan."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-3" data-testid="share-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Share2 className="w-5 h-5" /> Bagikan Daftar
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-share-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-5 overflow-y-auto">
          <p className="text-xs text-slate-500 -mt-1 truncate">Daftar: <span className="font-semibold text-slate-700">{list.name}</span></p>

          {/* Share code */}
          <div className="border border-slate-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="w-4 h-4 text-violet-600" />
              <h3 className="font-semibold text-sm text-slate-800">Kode Berbagi</h3>
            </div>
            {shareCode ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-2xl font-bold tracking-widest text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 text-center" data-testid="share-code-value">
                  {shareCode}
                </div>
                <Button size="sm" variant="outline" className="gap-1" onClick={copyCode} data-testid="copy-code-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <p className="text-xs text-slate-500 mb-2">Belum ada kode. Buat kode agar petugas lain bisa gabung mandiri.</p>
            )}
            <div className="flex gap-2 mt-2">
              <Button size="sm" className="gap-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={genCode} disabled={busy} data-testid="gen-code-btn">
                <RefreshCw className="w-3.5 h-3.5" /> {shareCode ? "Ganti Kode" : "Buat Kode"}
              </Button>
              {shareCode && (
                <Button size="sm" variant="outline" className="gap-1 text-rose-600 border-rose-200" onClick={disableCode} disabled={busy} data-testid="disable-code-btn">
                  <X className="w-3.5 h-3.5" /> Nonaktifkan
                </Button>
              )}
            </div>
          </div>

          {/* Current members */}
          <div className="border border-slate-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-sm text-slate-800">Anggota ({members.length})</h3>
            </div>
            {loading ? (
              <div className="text-center py-3 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Memuat…</div>
            ) : members.length === 0 ? (
              <p className="text-xs text-slate-500">Belum ada anggota. Tambahkan petugas di bawah atau bagikan kode.</p>
            ) : (
              <div className="space-y-1.5" data-testid="member-list">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`member-row-${m.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{m.name || m.username}</div>
                      <div className="text-[11px] text-slate-500">@{m.username} · {m.role}</div>
                    </div>
                    <button onClick={() => removeMember(m.id, m.name || m.username)} disabled={busy} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`remove-member-${m.id}-btn`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add members by picking users */}
          <div className="border border-slate-200 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-cyan-600" />
              <h3 className="font-semibold text-sm text-slate-800">Tambah Petugas</h3>
            </div>
            {loading ? null : addable.length === 0 ? (
              <p className="text-xs text-slate-500">Tidak ada akun petugas/kasir lain yang bisa ditambahkan.</p>
            ) : (
              <>
                <div className="max-h-40 overflow-y-auto space-y-1" data-testid="available-user-list">
                  {addable.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`avail-user-${u.id}`}>
                      <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleSel(u.id)} className="w-4 h-4 accent-cyan-600" data-testid={`avail-user-${u.id}-checkbox`} />
                      <div className="min-w-0">
                        <div className="text-sm text-slate-800 truncate">{u.name || u.username}</div>
                        <div className="text-[11px] text-slate-500">@{u.username} · {u.role}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <Button size="sm" className="gap-1 mt-2 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={addMembers} disabled={busy || selected.length === 0} data-testid="add-members-btn">
                  <Check className="w-3.5 h-3.5" /> Tambahkan ({selected.length})
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="p-4 border-t">
          <Button variant="outline" className="w-full" onClick={onClose} data-testid="close-share-btn">Tutup</Button>
        </div>
      </div>
    </div>
  );
}

// ============ JOIN MODAL ============
function JoinModal({ authToken, onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const c = code.trim();
    if (!c) { toast.error("Masukkan kode berbagi."); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/delivery-lists/share/join`, { code: c }, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(res.data?.already_member ? "Anda sudah menjadi anggota daftar ini." : `Berhasil bergabung: ${res.data?.list?.name || "daftar"}`);
      onJoined?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal bergabung.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-3" data-testid="join-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <LogIn className="w-5 h-5" /> Gabung Daftar Berbagi
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-join-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="text-xs font-semibold text-slate-700 block">Masukkan Kode Berbagi</label>
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            placeholder="mis: ABC123"
            maxLength={10}
            className="font-mono text-xl tracking-widest text-center uppercase"
            data-testid="join-code-input"
          />
          <p className="text-[11px] text-slate-500">Minta kode ini ke pembuat daftar. Setelah gabung, daftar akan muncul di halaman Antar Surat Anda.</p>
        </div>
        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="cancel-join-btn">Batal</Button>
          <Button className="flex-1 gap-1 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={submit} disabled={busy || !code.trim()} data-testid="submit-join-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Gabung
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ CREATE MODAL ============
function CreateListModal({ authToken, onClose, onCreated }) {
  const [name, setName] = useState(() => `Antar Surat ${new Date().toLocaleDateString("id-ID")}`);
  const [pasteText, setPasteText] = useState("");
  const [singleInput, setSingleInput] = useState("");
  const [chips, setChips] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Live combine: parse paste + chips
  const allIds = useMemo(() => {
    const fromPaste = parseIds(pasteText);
    const seen = new Set();
    const out = [];
    for (const cid of [...chips, ...fromPaste]) {
      if (seen.has(cid)) continue;
      seen.add(cid);
      out.push(cid);
    }
    return out;
  }, [pasteText, chips]);

  const addChip = () => {
    const cid = (singleInput || "").trim();
    if (!cid) return;
    setChips((prev) => (prev.includes(cid) ? prev : [...prev, cid]));
    setSingleInput("");
  };

  const removeChip = (cid) => setChips((prev) => prev.filter((c) => c !== cid));

  const [extracting, setExtracting] = useState(false);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    const tid = toast.loading("Memindai file...");
    try {
      const ids = await extractIdsFromFile(file, authToken);
      if (ids.length === 0) {
        toast.error("Tidak ada ID terdeteksi pada file.", { id: tid });
        return;
      }
      setPasteText((prev) => (prev ? `${prev}\n${ids.join("\n")}` : ids.join("\n")));
      toast.success(`${ids.length} ID terdeteksi dari file. Silakan cek dulu.`, { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memindai file.", { id: tid });
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return toast.error("Nama daftar wajib diisi.");
    if (allIds.length === 0) return toast.error("Tambahkan minimal 1 ID pelanggan.");
    setSaving(true);
    const tid = toast.loading("Membuat daftar...");
    try {
      const res = await axios.post(
        `${API}/delivery-lists`,
        { name: trimmedName, customer_ids: allIds },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`Daftar dibuat dengan ${allIds.length} ID.`, { id: tid });
      onCreated(res.data.list);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat daftar.", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-3" data-testid="create-list-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Plus className="w-5 h-5" /> Buat Daftar Antar Surat
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-create-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Nama Daftar</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="Mis: Antar Surat 30 Mei - Perumnas"
              data-testid="new-list-name-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Paste ID Pelanggan (pisah baris/koma/spasi)
            </label>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={"02140033\n01234567\n09876543"}
              rows={5}
              className="font-mono text-sm"
              data-testid="paste-ids-textarea"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => fileRef.current?.click()}
                data-testid="upload-file-btn"
                disabled={extracting}
              >
                {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Scan PDF / Excel / CSV
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.xlsx,.xlsm,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="upload-file-input"
              />
              {pasteText && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1 text-rose-600 border-rose-200"
                  onClick={() => setPasteText("")}
                  data-testid="clear-paste-btn"
                >
                  <X className="w-3.5 h-3.5" /> Hapus paste
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Tambah Satu per Satu</label>
            <div className="flex gap-2">
              <Input
                value={singleInput}
                onChange={(e) => setSingleInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChip(); } }}
                placeholder="Ketik ID lalu Enter"
                className="font-mono text-sm"
                data-testid="single-id-input"
              />
              <Button type="button" size="sm" onClick={addChip} data-testid="add-single-id-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {chips.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-mono border border-emerald-200"
                  >
                    {c}
                    <button onClick={() => removeChip(c)} className="hover:text-rose-600" data-testid={`remove-chip-${c}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-emerald-800">Total ID Unik Terdeteksi:</span>
            <span className="text-2xl font-bold text-emerald-700" data-testid="total-unique-count">{allIds.length}</span>
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="cancel-create-btn">
            Batal
          </Button>
          <Button
            className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSave}
            disabled={saving || allIds.length === 0 || !name.trim()}
            data-testid="save-list-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan ({allIds.length})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ DETAIL PAGE ============
function DeliveryListDetail({ listId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [minBulan, setMinBulan] = useState("");
  const [maxBulan, setMaxBulan] = useState("");
  const [checkingId, setCheckingId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [showAddIds, setShowAddIds] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lightboxPhoto, setLightboxPhoto] = useState(null); // dataURL of photo to preview
  const [showExport, setShowExport] = useState(false);
  const [showPhotoExport, setShowPhotoExport] = useState(false);
  const [showBulkCheck, setShowBulkCheck] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const authToken = localStorage.getItem("user_token") || localStorage.getItem("admin_token");

  const refreshPending = useCallback(async () => {
    setPendingSync(await getPendingQueueCount());
  }, []);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!authToken) return;
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${API}/delivery-lists/${listId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setData(res.data);
      saveDeliveryListOffline(res.data);
    } catch (e) {
      if (silent) return; // background poll: ignore transient errors
      // Offline / network error → load cached copy
      const cached = await getDeliveryListOffline(listId);
      if (cached) {
        setData(cached);
        toast.info("Mode offline: menampilkan data tersimpan.");
      } else {
        toast.error(e?.response?.data?.detail || "Gagal memuat daftar.");
        if (e?.response?.status === 404) navigate("/antar-surat");
      }
    } finally {
      if (!silent) setLoading(false);
      refreshPending();
    }
  }, [listId, authToken, navigate, refreshPending]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-flush queued offline edits when back online (and once on mount).
  useEffect(() => {
    const flush = async () => {
      setIsOffline(!navigator.onLine);
      if (!navigator.onLine) return;
      const before = await getPendingQueueCount();
      if (before === 0) { refreshPending(); return; }
      const r = await flushDeliveryQueue(API, authToken);
      if (r.synced > 0) {
        toast.success(`${r.synced} perubahan offline tersinkron.`);
        fetchData();
      }
      refreshPending();
    };
    flush();
    window.addEventListener("online", flush);
    const off = () => setIsOffline(true);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", flush);
      window.removeEventListener("offline", off);
    };
  }, [authToken, fetchData, refreshPending]);

  // Auto-refresh (~5s): detect status/photo changes made by shared members and
  // refetch silently so the list stays live without a manual reload.
  const lastUpdatedRef = useRef(null);
  useEffect(() => { if (data?.updated_at) lastUpdatedRef.current = data.updated_at; }, [data?.updated_at]);
  useEffect(() => {
    if (!authToken || !listId) return;
    let stop = false;
    const id = setInterval(async () => {
      if (stop || !navigator.onLine || document.hidden) return;
      try {
        const res = await axios.get(`${API}/delivery-lists/${listId}/state`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const srv = res.data?.updated_at;
        if (srv && srv !== lastUpdatedRef.current) {
          lastUpdatedRef.current = srv;
          fetchData({ silent: true });
        }
      } catch { /* ignore polling errors */ }
    }, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [authToken, listId, fetchData]);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    const min = minBulan === "" ? null : Number(minBulan);
    const max = maxBulan === "" ? null : Number(maxBulan);
    const monthActive = min !== null || max !== null;
    return data.items.filter((it) => {
      if (filter !== "all" && (it.status || "pending") !== filter) return false;
      if (monthActive) {
        const j = it.jumlah_tagihan;
        if (typeof j !== "number") return false; // belum dicek → sembunyikan saat filter bulan aktif
        if (min !== null && j < min) return false;
        if (max !== null && j > max) return false;
      }
      return true;
    });
  }, [data, filter, minBulan, maxBulan]);

  const handleCheckItem = async (cid) => {
    if (!navigator.onLine) { toast.error("Butuh koneksi internet untuk cek tagihan."); return; }
    setCheckingId(cid);
    const tid = toast.loading(`Cek tagihan ${cid}...`);
    try {
      const res = await axios.post(
        `${API}/delivery-lists/${listId}/items/${cid}/check`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      handleItemUpdated(res.data.item, res.data.counts);
      const it = res.data.item || {};
      toast.success(
        it.jumlah_tagihan === 0
          ? `${it.nama || cid}: Lunas ✅`
          : `${it.nama || cid}: ${it.jumlah_tagihan} bulan · Rp ${Number(it.total_unpaid || 0).toLocaleString("id-ID")}`,
        { id: tid }
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal cek tagihan.", { id: tid });
    } finally {
      setCheckingId(null);
    }
  };

  const handleItemUpdated = (updatedItem, newCounts) => {
    setData((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((it) =>
        it.customer_id === updatedItem.customer_id ? { ...it, ...updatedItem } : it
      );
      const counts = newCounts || computeCounts(items);
      const next = { ...prev, items, counts };
      saveDeliveryListOffline(next);
      return next;
    });
    refreshPending();
  };

  const handleRemoveItem = async (cid) => {
    if (!window.confirm(`Hapus ID ${cid} dari daftar?`)) return;
    try {
      await axios.delete(`${API}/delivery-lists/${listId}/items/${cid}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchData();
      toast.success("Item dihapus.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus.");
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(data.name || "antar-surat").replace(/[^\w-]+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Daftar di-download (backup offline).");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Memuat daftar...
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-emerald-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/antar-surat")} className="gap-1" data-testid="detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> Daftar
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-base text-emerald-700 truncate flex items-center gap-1.5" data-testid="detail-list-name">
              <FileText className="w-4 h-4" />
              {data.name}
            </h1>
            <p className="text-[11px] text-slate-500">{data.counts.total} ID · diperbarui {new Date(data.updated_at).toLocaleString("id-ID")}</p>
            {data.is_owner === false && (
              <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200" data-testid="detail-shared-badge">
                <Users className="w-3 h-3" /> Dibagikan oleh {data.owner_name || "pemilik"}
              </span>
            )}
            {data.is_owner && data.is_shared && (
              <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="detail-owner-badge">
                <Crown className="w-3 h-3" /> {data.member_count} anggota berbagi
              </span>
            )}
            {(isOffline || pendingSync > 0) && (
              <div className="flex items-center gap-1.5 mt-0.5" data-testid="offline-sync-indicator">
                {isOffline && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> Offline
                  </span>
                )}
                {pendingSync > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 inline-flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> {pendingSync} menunggu sync
                  </span>
                )}
              </div>
            )}
          </div>
          {data.is_owner && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
              onClick={() => setShowShare(true)}
              data-testid="detail-share-btn"
            >
              <Share2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => setShowRename(true)}
            data-testid="rename-list-btn"
          >
            <Edit3 className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Filter chips + counters */}
        <div className="max-w-4xl mx-auto px-4 pb-3 flex flex-wrap gap-1.5">
          {[
            { key: "all", label: `Semua ${data.counts.total}`, color: "bg-slate-100 text-slate-700 border-slate-300" },
            { key: "pending", label: `${STATUS_META.pending.emoji} Belum ${data.counts.pending}`, color: STATUS_META.pending.color },
            { key: "delivered", label: `${STATUS_META.delivered.emoji} Sudah ${data.counts.delivered}`, color: STATUS_META.delivered.color },
            { key: "promised_pay", label: `${STATUS_META.promised_pay.emoji} Janji Bayar ${data.counts.promised_pay || 0}`, color: STATUS_META.promised_pay.color },
            { key: "lunas", label: `${STATUS_META.lunas.emoji} Lunas ${data.counts.lunas || 0}`, color: STATUS_META.lunas.color },
            { key: "putus", label: `${STATUS_META.putus.emoji} Putus ${data.counts.putus || 0}`, color: STATUS_META.putus.color },
            { key: "not_found", label: `${STATUS_META.not_found.emoji} Tidak Ketemu ${data.counts.not_found}`, color: STATUS_META.not_found.color },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs px-3 py-1 rounded-full border transition ${
                filter === f.key
                  ? "bg-emerald-600 text-white border-emerald-700 shadow-sm"
                  : `${f.color} hover:opacity-80`
              }`}
              data-testid={`filter-${f.key}-btn`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Month (jumlah bulan tagihan) range filter */}
        <div className="max-w-4xl mx-auto px-4 pb-3 flex flex-wrap items-center gap-2" data-testid="month-filter-bar">
          <span className="text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1">
            <FilterIcon className="w-3.5 h-3.5" /> Filter bulan:
          </span>
          <Input
            type="number"
            min={0}
            value={minBulan}
            onChange={(e) => setMinBulan(e.target.value)}
            placeholder="min"
            className="h-8 w-20 text-xs"
            data-testid="filter-min-bulan-input"
          />
          <span className="text-slate-400 text-xs">–</span>
          <Input
            type="number"
            min={0}
            value={maxBulan}
            onChange={(e) => setMaxBulan(e.target.value)}
            placeholder="maks"
            className="h-8 w-20 text-xs"
            data-testid="filter-max-bulan-input"
          />
          <span className="text-[10px] text-slate-500">bulan</span>
          {(minBulan !== "" || maxBulan !== "") && (
            <button
              onClick={() => { setMinBulan(""); setMaxBulan(""); }}
              className="text-[11px] px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
              data-testid="clear-month-filter-btn"
            >
              Reset bulan
            </button>
          )}
          {(minBulan !== "" || maxBulan !== "") && (
            <span className="text-[10px] text-amber-600" data-testid="month-filter-hint">
              *hanya ID yang sudah dicek tagihannya yang tampil
            </span>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="max-w-4xl mx-auto px-4 pt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          className="gap-1 bg-cyan-600 hover:bg-cyan-700 text-white"
          onClick={() => {
            const params = new URLSearchParams({ antar_surat: listId });
            if (minBulan !== "") params.set("min_bulan", String(minBulan));
            if (maxBulan !== "") params.set("max_bulan", String(maxBulan));
            if (filter !== "all") params.set("as_status", filter);
            navigate(`/peta-global?${params.toString()}`);
          }}
          data-testid="open-on-map-btn"
        >
          <MapPin className="w-4 h-4" /> Buka di Peta Global
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1 border-sky-300 text-sky-700 hover:bg-sky-50"
          onClick={() => navigate(`/peta-global?antar_surat=${listId}&offline=1`)}
          data-testid="download-peta-offline-btn"
          title="Simpan peta area daftar ini untuk dipakai offline (hanya titik daftar ini)"
        >
          <Download className="w-4 h-4" /> Download Peta
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setShowAddIds(true)} data-testid="add-more-ids-btn">
          <Plus className="w-4 h-4" /> Tambah ID
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={handleDownload} data-testid="download-json-btn">
          <Download className="w-4 h-4" /> Download JSON
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          onClick={() => setShowExport(true)}
          data-testid="open-export-report-btn"
        >
          <FileDown className="w-4 h-4" /> Export Laporan
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
          onClick={() => setShowPhotoExport(true)}
          data-testid="open-export-photos-btn"
          title="Export foto saja — A4, 6 foto per halaman (PDF / Word)"
        >
          <ImageIcon className="w-4 h-4" /> Export Foto
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1 border-cyan-400 text-cyan-700 hover:bg-cyan-50"
          onClick={() => setShowBulkCheck(true)}
          data-testid="open-bulk-check-btn"
          title="Cek tagihan semua ID di daftar ini (satu per satu, teliti)"
        >
          <SearchIcon className="w-4 h-4" /> Cek Semua Tagihan
        </Button>
        <Button size="sm" variant="outline" className="gap-1" onClick={fetchData} data-testid="refresh-list-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Items */}
      <div className="max-w-4xl mx-auto p-4 space-y-2">
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
            Tidak ada item dengan filter ini.
          </div>
        ) : (
          filteredItems.map((it) => {
            const s = it.status || "pending";
            const meta = STATUS_META[s] || STATUS_META.pending;
            return (
              <div
                key={it.customer_id}
                className="bg-white rounded-xl border border-slate-200 p-3 flex items-start gap-3 hover:border-emerald-300 transition"
                data-testid={`delivery-item-${it.customer_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-mono font-bold text-sm text-slate-800">{it.customer_id}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.color}`}>
                      {meta.emoji} {meta.label}
                    </span>
                    {s === "promised_pay" && it.promised_date && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {it.promised_date}
                      </span>
                    )}
                    {DATED_STATUSES.includes(s) && it.status_date && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-200 inline-flex items-center gap-1" data-testid={`status-date-${it.customer_id}`}>
                        <Calendar className="w-3 h-3" /> {it.status_date}
                      </span>
                    )}
                  </div>
                  {(it.nama || typeof it.jumlah_tagihan === "number") && (
                    <div className="text-sm text-slate-700 truncate flex items-center gap-1.5 flex-wrap">
                      {it.nama && <span className="truncate">{it.nama}</span>}
                      {typeof it.jumlah_tagihan === "number" && (
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border shrink-0 inline-flex items-center gap-1 ${
                            it.jumlah_tagihan === 0
                              ? "bg-teal-50 text-teal-700 border-teal-200"
                              : "bg-rose-50 text-rose-700 border-rose-200"
                          }`}
                          title={it.checked_at ? `Dicek: ${new Date(it.checked_at).toLocaleString("id-ID")}` : undefined}
                          data-testid={`tagihan-info-${it.customer_id}`}
                        >
                          {it.jumlah_tagihan === 0
                            ? "💰 Lunas"
                            : `🔴 ${it.jumlah_tagihan} tagihan · Rp ${Number(it.total_unpaid || 0).toLocaleString("id-ID")}`}
                        </span>
                      )}
                    </div>
                  )}
                  {it.alamat && <div className="text-xs text-slate-500 truncate">{it.alamat}</div>}
                  {it.note && <div className="text-[11px] text-amber-700 italic mt-1">📝 {it.note}</div>}
                  {itemPhotos(it).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`photos-${it.customer_id}`}>
                      {itemPhotos(it).map((ph, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLightboxPhoto({ src: ph, title: `${it.customer_id} · ${it.nama || ""} (${idx + 1})` }); }}
                          className="block group"
                          data-testid={`open-photo-${it.customer_id}-${idx}-btn`}
                          title="Klik untuk perbesar"
                        >
                          <img
                            src={ph}
                            alt={`bukti ${idx + 1}`}
                            className="w-12 h-12 object-cover rounded border group-hover:ring-2 group-hover:ring-emerald-400 transition cursor-zoom-in"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs h-8 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    onClick={() => handleCheckItem(it.customer_id)}
                    disabled={checkingId === it.customer_id}
                    data-testid={`check-bill-${it.customer_id}-btn`}
                    title="Cek tagihan PDAM & isi nama otomatis untuk ID ini"
                  >
                    {checkingId === it.customer_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <SearchIcon className="w-3 h-3" />} Cek
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1 text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => navigate(`/pdam-tebo?id=${encodeURIComponent(it.customer_id)}&from=antar_surat&list=${listId}`)}
                    data-testid={`pay-now-${it.customer_id}-btn`}
                    title="Bayar tagihan pelanggan ini sekarang"
                  >
                    <HandCoins className="w-3 h-3" /> Bayar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-xs h-8"
                    onClick={() => setEditItem(it)}
                    data-testid={`edit-item-${it.customer_id}-btn`}
                  >
                    <Edit3 className="w-3 h-3" /> Ubah
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1 text-xs h-8 text-rose-600 hover:bg-rose-50"
                    onClick={() => handleRemoveItem(it.customer_id)}
                    data-testid={`remove-item-${it.customer_id}-btn`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {editItem && (
        <EditItemModal
          item={editItem}
          listId={listId}
          authToken={authToken}
          onClose={() => setEditItem(null)}
          onUpdated={(updatedItem, newCounts) => {
            handleItemUpdated(updatedItem, newCounts);
            setEditItem(null);
          }}
        />
      )}

      {showAddIds && (
        <AddIdsModal
          listId={listId}
          authToken={authToken}
          existingIds={data.items.map((i) => i.customer_id)}
          onClose={() => setShowAddIds(false)}
          onAdded={() => { setShowAddIds(false); fetchData(); }}
        />
      )}

      {showRename && (
        <RenameModal
          currentName={data.name}
          listId={listId}
          authToken={authToken}
          onClose={() => setShowRename(false)}
          onRenamed={(newName) => {
            setData((prev) => ({ ...prev, name: newName }));
            setShowRename(false);
          }}
        />
      )}

      {lightboxPhoto && (
        <PhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />
      )}

      {showExport && (
        <ExportReportModal
          list={data}
          onClose={() => setShowExport(false)}
        />
      )}

      {showPhotoExport && (
        <PhotoExportModal
          list={data}
          onClose={() => setShowPhotoExport(false)}
        />
      )}

      {showBulkCheck && (
        <BulkCheckModal
          list={data}
          listId={listId}
          authToken={authToken}
          onClose={() => setShowBulkCheck(false)}
          onItemUpdated={handleItemUpdated}
        />
      )}

      {showShare && (
        <ShareModal
          authToken={authToken}
          list={{ id: listId, name: data.name, share_code: data.share_code }}
          onClose={() => setShowShare(false)}
          onChanged={fetchData}
        />
      )}
    </div>
  );
}

// ============ EDIT ITEM MODAL ============
function EditItemModal({ item, listId, authToken, onClose, onUpdated }) {
  const [status, setStatus] = useState(item.status || "pending");
  const [note, setNote] = useState(item.note || "");
  const [photos, setPhotos] = useState(itemPhotos(item));
  const initialPhotosRef = useRef(itemPhotos(item));
  const [promisedDate, setPromisedDate] = useState(item.promised_date || "");
  const [statusDate, setStatusDate] = useState(item.status_date || "");
  const [saving, setSaving] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const fileRef = useRef(null);

  // Auto-fill today's date when switching to a dated status and none is set yet.
  useEffect(() => {
    if (DATED_STATUSES.includes(status) && !statusDate) {
      setStatusDate(new Date().toISOString().slice(0, 10));
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 5 - photos.length;
    if (room <= 0) { toast.error("Maksimum 5 foto per ID."); return; }
    const take = files.slice(0, room);
    if (files.length > room) toast.info(`Hanya ${room} foto ditambahkan (maks 5).`);
    try {
      const dataUrls = [];
      for (const f of take) {
        if (!f.type.startsWith("image/")) continue;
        dataUrls.push(await compressImage(f, 800, 0.75));
      }
      setPhotos((prev) => [...prev, ...dataUrls].slice(0, 5));
    } catch (err) {
      toast.error("Gagal memproses foto.");
    }
  };

  const handleSave = async () => {
    if (status === "promised_pay" && !promisedDate) {
      toast.error("Pilih tanggal janji bayar terlebih dulu.");
      return;
    }
    setSaving(true);
    const promised = status === "promised_pay" ? promisedDate : "";
    const sDate = DATED_STATUSES.includes(status) ? statusDate : "";
    const base = { status, note, promised_date: promised, status_date: sDate };
    const initial = initialPhotosRef.current || [];
    const added = photos.filter((p) => !initial.includes(p));
    const removed = initial.filter((p) => !photos.includes(p));
    // Offline → queue a full snapshot (single device, no concurrency) & update UI optimistically
    if (!navigator.onLine) {
      const offlinePayload = { ...base, photos };
      await enqueueDeliveryEdit(listId, item.customer_id, offlinePayload);
      toast.success("Tersimpan offline. Akan otomatis tersinkron saat online.");
      onUpdated({ customer_id: item.customer_id, ...offlinePayload, _pendingSync: true }, null);
      setSaving(false);
      return;
    }
    // Online → send only the photo DELTA so a concurrent editor's photos aren't clobbered.
    const payload = { ...base };
    if (added.length) payload.photos_add = added;
    if (removed.length) payload.photos_remove = removed;
    const tid = toast.loading("Menyimpan...");
    try {
      const res = await axios.patch(
        `${API}/delivery-lists/${listId}/items/${item.customer_id}`,
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Tersimpan.", { id: tid });
      initialPhotosRef.current = itemPhotos(res.data.item);
      setPhotos(itemPhotos(res.data.item));
      onUpdated(res.data.item, res.data.counts);
    } catch (err) {
      // Network failure → fall back to offline queue
      if (!err?.response) {
        const offlinePayload = { ...base, photos };
        await enqueueDeliveryEdit(listId, item.customer_id, offlinePayload);
        toast.success("Koneksi terputus — disimpan offline & akan tersinkron otomatis.", { id: tid });
        onUpdated({ customer_id: item.customer_id, ...offlinePayload, _pendingSync: true }, null);
      } else {
        toast.error(err?.response?.data?.detail || "Gagal menyimpan.", { id: tid });
      }
    } finally {
      setSaving(false);
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="edit-item-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <Edit3 className="w-4 h-4" /> Status Antar Surat
            </h2>
            <p className="text-xs opacity-90 font-mono truncate">{item.customer_id} · {item.nama || "-"}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-edit-item-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "pending", label: "Belum", icon: Circle, color: "border-rose-300 text-rose-700 hover:bg-rose-50", active: "bg-rose-600 border-rose-700" },
                { key: "delivered", label: "Sudah", icon: CheckCircle2, color: "border-emerald-300 text-emerald-700 hover:bg-emerald-50", active: "bg-emerald-600 border-emerald-700" },
                { key: "not_found", label: "Tidak Ketemu", icon: XCircle, color: "border-slate-300 text-slate-600 hover:bg-slate-50", active: "bg-slate-600 border-slate-700" },
                { key: "promised_pay", label: "Janji Bayar", icon: Calendar, color: "border-sky-300 text-sky-700 hover:bg-sky-50", active: "bg-sky-600 border-sky-700" },
                { key: "lunas", label: "Lunas", icon: HandCoins, color: "border-teal-300 text-teal-700 hover:bg-teal-50", active: "bg-teal-600 border-teal-700" },
                { key: "putus", label: "Putus", icon: Scissors, color: "border-purple-300 text-purple-700 hover:bg-purple-50", active: "bg-purple-600 border-purple-700" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setStatus(opt.key)}
                  className={`px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-1 text-xs font-semibold transition ${
                    status === opt.key
                      ? `${opt.active} text-white shadow-sm`
                      : `bg-white ${opt.color}`
                  }`}
                  data-testid={`status-${opt.key}-btn`}
                >
                  <opt.icon className="w-5 h-5" />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {status === "promised_pay" && (
            <div data-testid="promised-date-section">
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Tanggal Janji Bayar <span className="text-rose-600">*</span>
              </label>
              <Input
                type="date"
                value={promisedDate}
                min={todayIso}
                onChange={(e) => setPromisedDate(e.target.value)}
                className="text-sm"
                data-testid="promised-date-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Pilih tanggal pelanggan berjanji akan membayar tagihan.
              </p>
            </div>
          )}

          {DATED_STATUSES.includes(status) && (
            <div data-testid="status-date-section">
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Tanggal {STATUS_META[status]?.label || "Status"}
              </label>
              <Input
                type="date"
                value={statusDate}
                max={todayIso}
                onChange={(e) => setStatusDate(e.target.value)}
                className="text-sm"
                data-testid="status-date-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Tanggal status ini ditetapkan (default hari ini, bisa diubah).
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Catatan (opsional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Mis: Diterima istri, ditempel di pintu, dll."
              data-testid="edit-note-input"
            />
            <div className="text-[10px] text-slate-400 text-right">{note.length}/500</div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">
              Foto Bukti (opsional) <span className="text-slate-400 font-normal">· {photos.length}/5</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((ph, idx) => (
                <div className="relative" key={idx}>
                  <img
                    src={ph}
                    alt={`bukti ${idx + 1}`}
                    onClick={() => setLightboxIdx(idx)}
                    className="w-full h-24 object-cover bg-slate-100 rounded-lg border cursor-zoom-in hover:opacity-90 transition"
                    data-testid={`edit-photo-preview-${idx}`}
                  />
                  <button
                    className="absolute top-1 right-1 bg-rose-600 text-white rounded-full p-1 shadow"
                    onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                    data-testid={`remove-photo-${idx}-btn`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1 h-full min-h-[3rem]"
                    onClick={() => { fileRef.current.setAttribute("capture", "environment"); fileRef.current.click(); }}
                    data-testid="add-photo-camera-btn"
                  >
                    <Camera className="w-3.5 h-3.5" /> Kamera
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 gap-1"
                    onClick={() => { fileRef.current.removeAttribute("capture"); fileRef.current.click(); }}
                    data-testid="add-photo-gallery-btn"
                  >
                    <ImageIcon className="w-3.5 h-3.5" /> Galeri
                  </Button>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="cancel-edit-item-btn">
            Batal
          </Button>
          <Button
            className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-edit-item-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Simpan
          </Button>
        </div>
      </div>

      {lightboxIdx !== null && photos[lightboxIdx] && (
        <PhotoLightbox
          photo={{ src: photos[lightboxIdx], title: `${item.customer_id} · ${item.nama || ""} (${lightboxIdx + 1}/${photos.length})` }}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// ============ PHOTO LIGHTBOX ============
function PhotoLightbox({ photo, onClose }) {
  // Close on Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDownload = () => {
    try {
      const a = document.createElement("a");
      a.href = photo.src;
      a.download = `bukti_${(photo.title || "foto").replace(/[^\w-]+/g, "_")}.jpg`;
      a.click();
    } catch {/* ignore */}
  };

  return (
    <div
      className="fixed inset-0 z-[2200] bg-black/90 flex flex-col items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <div className="absolute top-3 right-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={handleDownload}
          className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
          data-testid="lightbox-download-btn"
        >
          <Download className="w-3.5 h-3.5" /> Simpan
        </button>
        <button
          onClick={onClose}
          className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-lg"
          data-testid="lightbox-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      {photo.title && (
        <div className="text-white text-xs font-mono mb-2 opacity-80" data-testid="lightbox-title">
          {photo.title}
        </div>
      )}
      <img
        src={photo.src}
        alt="bukti besar"
        className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <p className="text-white/60 text-[11px] mt-3">Tap area gelap atau tekan ESC untuk menutup</p>
    </div>
  );
}

// ============ EXPORT REPORT MODAL ============
function ExportReportModal({ list, onClose }) {
  const [photoSize, setPhotoSize] = useState("medium");
  const [format, setFormat] = useState("pdf");
  const [busy, setBusy] = useState(false);
  const ALL_STATUSES = ["pending", "delivered", "promised_pay", "lunas", "putus", "not_found"];
  const [selectedStatuses, setSelectedStatuses] = useState(ALL_STATUSES);

  const toggleStatus = (key) =>
    setSelectedStatuses((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );

  const includedCount = useMemo(
    () => (list?.items || []).filter((it) => selectedStatuses.includes(it.status || "pending")).length,
    [list, selectedStatuses]
  );

  const handleExport = async () => {
    if (busy) return;
    if (selectedStatuses.length === 0) {
      toast.error("Pilih minimal satu status untuk diekspor.");
      return;
    }
    if (includedCount === 0) {
      toast.error("Tidak ada data untuk status yang dipilih.");
      return;
    }
    setBusy(true);
    const tid = toast.loading(`Membuat laporan ${format.toUpperCase()}...`);
    try {
      if (format === "pdf") {
        await exportDeliveryPdf(list, photoSize, selectedStatuses);
      } else {
        await exportDeliveryDocx(list, photoSize, selectedStatuses);
      }
      toast.success("Laporan berhasil dibuat.", { id: tid });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Gagal membuat laporan: " + (err?.message || "unknown"), { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const statusOptions = [
    { key: "pending", label: "Belum", emoji: "🔴" },
    { key: "delivered", label: "Sudah", emoji: "🟢" },
    { key: "promised_pay", label: "Janji Bayar", emoji: "🔵" },
    { key: "lunas", label: "Lunas", emoji: "💰" },
    { key: "putus", label: "Putus", emoji: "✂️" },
    { key: "not_found", label: "Tidak Ketemu", emoji: "⚫" },
  ];

  const sizeOptions = [
    { key: "none", label: "Tanpa Foto", desc: "Hanya teks (paling ringan)" },
    { key: "small", label: "Kecil", desc: "± 2 cm" },
    { key: "medium", label: "Sedang", desc: "± 3 cm (default)" },
    { key: "large", label: "Besar", desc: "± 4,5 cm" },
  ];

  const counts = list?.counts || {};

  return (
    <div className="fixed inset-0 z-[2100] bg-black/60 flex items-center justify-center p-3" data-testid="export-report-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <FileDown className="w-4 h-4" /> Export Laporan
            </h2>
            <p className="text-xs opacity-90 truncate">{list?.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-export-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 space-y-0.5">
            <div className="font-semibold" data-testid="export-included-count">{includedCount} dari {list?.counts?.total || 0} ID akan disertakan</div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
              <span>🔴 Belum {counts.pending || 0}</span>
              <span>🟢 Sudah {counts.delivered || 0}</span>
              <span>🔵 Janji {counts.promised_pay || 0}</span>
              <span>💰 Lunas {counts.lunas || 0}</span>
              <span>✂️ Putus {counts.putus || 0}</span>
              <span>⚫ Tdk Ketemu {counts.not_found || 0}</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-700">Filter Status (centang yang diekspor)</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedStatuses(ALL_STATUSES)}
                  className="text-[10px] px-2 py-0.5 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="export-select-all-status-btn"
                >
                  Semua
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedStatuses([])}
                  className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                  data-testid="export-clear-status-btn"
                >
                  Kosongkan
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((opt) => {
                const active = selectedStatuses.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => toggleStatus(opt.key)}
                    className={`px-2 py-2 rounded-lg border-2 flex items-center gap-2 text-xs font-semibold transition ${
                      active
                        ? "bg-emerald-600 border-emerald-700 text-white shadow-sm"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`export-status-${opt.key}-btn`}
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${active ? "bg-white/20 border-white" : "border-slate-400"}`}>
                      {active ? "✓" : ""}
                    </span>
                    <span>{opt.emoji} {opt.label}</span>
                    <span className={`ml-auto ${active ? "opacity-90" : "text-slate-400"}`}>{counts[opt.key] || 0}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Format File</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "pdf", label: "PDF", desc: "Rapi & siap cetak" },
                { key: "docx", label: "Word (.docx)", desc: "Bisa diedit ulang" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFormat(opt.key)}
                  className={`px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-0.5 text-xs font-semibold transition ${
                    format === opt.key
                      ? "bg-emerald-600 border-emerald-700 text-white shadow-sm"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`export-format-${opt.key}-btn`}
                >
                  <span>{opt.label}</span>
                  <span className={`text-[10px] font-normal ${format === opt.key ? "opacity-90" : "text-slate-500"}`}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Ukuran Foto Bukti</label>
            <div className="grid grid-cols-2 gap-2">
              {sizeOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPhotoSize(opt.key)}
                  className={`px-2 py-2 rounded-lg border-2 flex flex-col items-start text-left gap-0 text-xs font-semibold transition ${
                    photoSize === opt.key
                      ? "bg-cyan-600 border-cyan-700 text-white shadow-sm"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`photo-size-${opt.key}-btn`}
                >
                  <span>{opt.label}</span>
                  <span className={`text-[10px] font-normal ${photoSize === opt.key ? "opacity-90" : "text-slate-500"}`}>{opt.desc}</span>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Untuk daftar &gt; 50 item dengan foto Besar, proses pembuatan bisa memakan waktu lebih lama.
            </p>
          </div>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="cancel-export-btn">
            Batal
          </Button>
          <Button
            className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleExport}
            disabled={busy || selectedStatuses.length === 0 || includedCount === 0}
            data-testid="confirm-export-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Download {format.toUpperCase()} ({includedCount})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ PHOTO EXPORT MODAL ============
function PhotoExportModal({ list, onClose }) {
  const [format, setFormat] = useState("pdf");
  const [busy, setBusy] = useState(false);

  const photoCount = useMemo(
    () => (list?.items || []).reduce((n, it) => n + itemPhotos(it).length, 0),
    [list]
  );
  const pageCount = Math.max(1, Math.ceil(photoCount / 6));

  const handleExport = async () => {
    if (busy) return;
    if (photoCount === 0) { toast.error("Belum ada foto di daftar ini."); return; }
    setBusy(true);
    const tid = toast.loading(`Menyusun foto ke ${format.toUpperCase()}...`);
    try {
      if (format === "pdf") await exportDeliveryPhotosPdf(list);
      else await exportDeliveryPhotosDocx(list);
      toast.success("File foto berhasil dibuat.", { id: tid });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Gagal membuat file: " + (err?.message || "unknown"), { id: tid });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2100] bg-black/60 flex items-center justify-center p-3" data-testid="photo-export-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Export Foto
            </h2>
            <p className="text-xs opacity-90 truncate">{list?.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-photo-export-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-xs text-fuchsia-800 space-y-0.5">
            <div className="font-semibold" data-testid="photo-export-count">{photoCount} foto · {pageCount} halaman A4</div>
            <div>Tata letak 2 kolom × 3 baris (6 foto per halaman), foto saja tanpa keterangan.</div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-2">Format File</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: "pdf", label: "PDF", desc: "Rapi & siap cetak" },
                { key: "docx", label: "Word (.docx)", desc: "Bisa diedit ulang" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFormat(opt.key)}
                  className={`px-2 py-3 rounded-lg border-2 flex flex-col items-center gap-0.5 text-xs font-semibold transition ${
                    format === opt.key
                      ? "bg-fuchsia-600 border-fuchsia-700 text-white shadow-sm"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`photo-export-format-${opt.key}-btn`}
                >
                  <span>{opt.label}</span>
                  <span className={`text-[10px] font-normal ${format === opt.key ? "opacity-90" : "text-slate-500"}`}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <p className="text-[10px] text-slate-500">
            Semua foto dari setiap ID disertakan (maks 5 per ID). Daftar dengan banyak foto bisa butuh waktu beberapa detik.
          </p>
        </div>

        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy} data-testid="cancel-photo-export-btn">
            Batal
          </Button>
          <Button
            className="flex-1 gap-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            onClick={handleExport}
            disabled={busy || photoCount === 0}
            data-testid="confirm-photo-export-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Download {format.toUpperCase()} ({photoCount})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ ADD IDS MODAL ============
function AddIdsModal({ listId, authToken, existingIds, onClose, onAdded }) {
  const [pasteText, setPasteText] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const existingSet = useMemo(() => new Set(existingIds), [existingIds]);
  const newIds = useMemo(() => {
    return parseIds(pasteText).filter((cid) => !existingSet.has(cid));
  }, [pasteText, existingSet]);
  const duplicateCount = useMemo(() => {
    return parseIds(pasteText).length - newIds.length;
  }, [pasteText, newIds]);

  const [extracting, setExtracting] = useState(false);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setExtracting(true);
    const tid = toast.loading("Memindai file...");
    try {
      const ids = await extractIdsFromFile(file, authToken);
      if (ids.length === 0) {
        toast.error("Tidak ada ID terdeteksi pada file.", { id: tid });
        return;
      }
      setPasteText((prev) => (prev ? `${prev}\n${ids.join("\n")}` : ids.join("\n")));
      toast.success(`${ids.length} ID terdeteksi dari file. Silakan cek dulu.`, { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memindai file.", { id: tid });
    } finally {
      setExtracting(false);
    }
  };

  const handleAdd = async () => {
    if (newIds.length === 0) return toast.error("Tidak ada ID baru.");
    setSaving(true);
    const tid = toast.loading("Menambahkan ID...");
    try {
      const res = await axios.post(
        `${API}/delivery-lists/${listId}/items`,
        { customer_ids: newIds },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success(`${res.data.added} ID ditambahkan.`, { id: tid });
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menambah ID.", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="add-ids-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-emerald-600 text-white p-4 flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Plus className="w-5 h-5" /> Tambah ID
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-add-ids-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={6}
            placeholder={"Paste ID (pisah baris/koma/spasi)\n02140033\n01234567"}
            className="font-mono text-sm"
            data-testid="add-paste-textarea"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="gap-1" data-testid="add-upload-file-btn" disabled={extracting}>
              {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Scan PDF / Excel / CSV
            </Button>
            <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xlsm,.csv,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv" onChange={handleFileUpload} className="hidden" />
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm flex justify-between items-center">
            <span>ID baru:</span>
            <span className="font-bold text-emerald-700 text-xl" data-testid="new-ids-count">{newIds.length}</span>
          </div>
          {duplicateCount > 0 && (
            <p className="text-[11px] text-amber-600">{duplicateCount} ID sudah ada di daftar, akan dilewati.</p>
          )}
        </div>
        <div className="p-4 border-t flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="cancel-add-ids-btn">Batal</Button>
          <Button
            className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={handleAdd}
            disabled={saving || newIds.length === 0}
            data-testid="confirm-add-ids-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Tambah ({newIds.length})
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============ RENAME MODAL ============
function RenameModal({ currentName, listId, authToken, onClose, onRenamed }) {
  const [name, setName] = useState(currentName);
  const [saving, setSaving] = useState(false);

  const handleRename = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Nama wajib diisi.");
    setSaving(true);
    try {
      await axios.put(
        `${API}/delivery-lists/${listId}`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Nama daftar diubah.");
      onRenamed(trimmed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal rename.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="rename-list-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="bg-emerald-600 text-white p-3 flex items-center justify-between">
          <h2 className="font-bold text-sm">Ubah Nama Daftar</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="close-rename-modal">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="Nama daftar"
            autoFocus
            data-testid="rename-input"
          />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={saving} data-testid="cancel-rename-btn">Batal</Button>
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleRename} disabled={saving} data-testid="confirm-rename-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ BULK CHECK MODAL (Cek Semua Tagihan) ============
// Runs the PDAM Tebo bill-check for every ID in the delivery list, one by one,
// with a 20-second timeout per ID and up to 3 retries on transient failures.
// When total_unpaid == 0 for an ID, its Antar Surat status is auto-set to "lunas".
function BulkCheckModal({ list, listId, authToken, onClose, onItemUpdated }) {
  const items = useMemo(() => list?.items || [], [list]);
  const total = items.length;
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [batchTotal, setBatchTotal] = useState(0); // size of CURRENT batch (fresh or retry) — used for progress denom
  const [current, setCurrent] = useState(null); // {index, id, nama, attempt}
  const [results, setResults] = useState([]); // [{id, nama, ok, lunas, total_unpaid, jumlah, error, statusUpdated}]
  const [onlyPending, setOnlyPending] = useState(true);
  const [savedAt, setSavedAt] = useState(null); // ISO timestamp of the last saved scan (restored from localStorage or set on completion)
  const abortRef = useRef(false);
  const cacheKey = `bulk_check_last_result_${listId}`;

  // Restore last scan result from localStorage when modal opens.
  // Guarded so it only runs once per (listId, cacheKey).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (!cached || !Array.isArray(cached.results) || cached.results.length === 0) return;
      setResults(cached.results);
      setBatchTotal(Number(cached.batchTotal) || cached.results.length);
      setProcessed(Number(cached.processed) || cached.results.length);
      setDone(true);
      setSavedAt(cached.savedAt || null);
    } catch { /* ignore corrupted cache */ }
  }, [cacheKey]);

  const targetItems = useMemo(
    () => (onlyPending ? items.filter((it) => (it.status || "pending") !== "lunas") : items),
    [items, onlyPending]
  );

  const summary = useMemo(() => {
    const s = { lunas: 0, unpaid: 0, error: 0 };
    for (const r of results) {
      if (!r.ok) s.error += 1;
      else if (r.lunas) s.lunas += 1;
      else s.unpaid += 1;
    }
    return s;
  }, [results]);

  const runOne = useCallback(async (cid, attempt = 1) => {
    try {
      const res = await axios.post(
        `${API}/pdam-tebo/check`,
        { no_sambungan: cid, nama: "" },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 20000, // 20 seconds per ID as requested
        }
      );
      const totalUnpaid = Number(res.data?.total_unpaid || 0);
      const jml = Number(res.data?.jumlah_tagihan || 0);
      // Fully paid = zero unpaid bills on the server (local_paid_bills doesn't matter here —
      // if server shows 0 unpaid AND 0 bills, it's truly lunas).
      const lunas = totalUnpaid === 0 && jml === 0;
      return {
        ok: true,
        lunas,
        total_unpaid: totalUnpaid,
        jumlah: jml,
        nama: res.data?.pelanggan?.nama || "",
      };
    } catch (err) {
      const status = err?.response?.status;
      // Retry on timeout / network / 5xx / 503 — up to 3 attempts, so no data is left inconsistent.
      const transient = !err?.response || status >= 500 || err.code === "ECONNABORTED";
      if (attempt < 3 && transient) {
        setCurrent((c) => (c ? { ...c, attempt: attempt + 1 } : c));
        await new Promise((r) => setTimeout(r, 1500));
        return runOne(cid, attempt + 1);
      }
      return {
        ok: false,
        error: err?.response?.data?.detail || err?.message || "Gagal cek tagihan.",
      };
    }
  }, [authToken]);

  // Core batch loop — runs runOne() for each item in `batch`, patches successful ones,
  // and updates `results` state. When `isRetry` is true, existing results are preserved
  // and only the rechecked IDs are replaced in place (so the results list stays complete).
  const runBatch = useCallback(async (batch, { isRetry = false } = {}) => {
    if (running || !batch || batch.length === 0) return;
    setRunning(true);
    setDone(false);
    setBatchTotal(batch.length);
    abortRef.current = false;

    // Retry: preserve existing results and replace-by-id; fresh run: reset.
    const baseResults = isRetry ? [...results] : [];
    if (!isRetry) setResults([]);
    setProcessed(0);

    const upsertResult = (entry) => {
      const idx = baseResults.findIndex((x) => x.id === entry.id);
      if (idx >= 0) baseResults[idx] = entry;
      else baseResults.push(entry);
      setResults([...baseResults]);
    };

    let processedCount = 0;
    for (let i = 0; i < batch.length; i++) {
      if (abortRef.current) break;
      const it = batch[i];
      setCurrent({ index: i, id: it.customer_id, nama: it.nama || "", attempt: 1 });
      const r = await runOne(it.customer_id);
      let statusUpdated = false;
      const nowIso = new Date().toISOString();
      // For EVERY successful check, persist the tagihan snapshot on the item so the
      // count/total shows up in the list card ("berapa tagihan"). If lunas → also flip status.
      if (r.ok) {
        const patchBody = {
          total_unpaid: r.total_unpaid,
          jumlah_tagihan: r.jumlah,
          checked_at: nowIso,
        };
        if (r.lunas && (it.status || "pending") !== "lunas") {
          patchBody.status = "lunas";
        }
        try {
          const upd = await axios.patch(
            `${API}/delivery-lists/${listId}/items/${it.customer_id}`,
            patchBody,
            { headers: { Authorization: `Bearer ${authToken}` }, timeout: 15000 }
          );
          statusUpdated = !!patchBody.status;
          if (onItemUpdated) onItemUpdated(upd.data.item, upd.data.counts);
        } catch (e) {
          r.syncError = e?.response?.data?.detail || e?.message || "Sync gagal";
        }
      }
      upsertResult({ id: it.customer_id, nama: r.nama || it.nama || "", ...r, statusUpdated });
      processedCount += 1;
      setProcessed(processedCount);
    }

    setRunning(false);
    setDone(true);
    setCurrent(null);

    // Persist final results to localStorage (per list) so user can resume the retry-failed flow
    // after accidentally closing the modal or navigating away.
    const finishedAt = new Date().toISOString();
    setSavedAt(finishedAt);
    try {
      localStorage.setItem(cacheKey, JSON.stringify({
        results: baseResults,
        batchTotal: batch.length,
        processed: baseResults.length,
        savedAt: finishedAt,
      }));
    } catch { /* quota / private-mode → ignore, non-fatal */ }

    const okLunas = baseResults.filter((x) => x.ok && x.lunas).length;
    const okUnpaid = baseResults.filter((x) => x.ok && !x.lunas).length;
    const errCount = baseResults.filter((x) => !x.ok).length;
    if (abortRef.current) {
      toast.info("Cek dihentikan.");
    } else if (isRetry) {
      const retriedOk = batch.length - errCount;
      toast.success(`Retry selesai: ${retriedOk} berhasil, ${errCount} masih gagal.`);
    } else {
      toast.success(`Selesai: ${okLunas} lunas, ${okUnpaid} masih menunggak, ${errCount} gagal.`);
    }
  }, [running, results, runOne, listId, authToken, onItemUpdated, cacheKey]);

  const start = useCallback(() => runBatch(targetItems, { isRetry: false }), [runBatch, targetItems]);

  // Retry only the failed IDs from the previous run. Uses the same runOne (with 3× retry
  // per ID) so transient failures during retry get the same protection.
  const failedResults = useMemo(() => results.filter((r) => !r.ok), [results]);
  const retryFailed = useCallback(() => {
    if (running || failedResults.length === 0) return;
    const failedIds = new Set(failedResults.map((r) => r.id));
    // Rebuild the item objects (customer_id + status/nama) from the current list so
    // the "lunas → auto-set status" logic works correctly during retry.
    const failedItems = items.filter((it) => failedIds.has(it.customer_id));
    if (failedItems.length === 0) return;
    runBatch(failedItems, { isRetry: true });
  }, [running, failedResults, items, runBatch]);

  const stop = () => { abortRef.current = true; };

  // Clear the cached scan result and reset the modal to fresh state (pre-run).
  const clearCache = useCallback(() => {
    try { localStorage.removeItem(cacheKey); } catch { /* ignore */ }
    setResults([]);
    setProcessed(0);
    setBatchTotal(0);
    setDone(false);
    setSavedAt(null);
    toast.info("Hasil terakhir dibersihkan.");
  }, [cacheKey]);

  const pct = batchTotal > 0 ? Math.round((processed / batchTotal) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" data-testid="bulk-check-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-cyan-600 to-sky-700 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="font-bold text-base flex items-center gap-2">
              <SearchIcon className="w-4 h-4" /> Cek Semua Tagihan
            </h2>
            <p className="text-xs opacity-90 truncate">{list?.name} · {total} ID · timeout 20 detik/ID</p>
          </div>
          <button
            onClick={onClose}
            disabled={running}
            className="p-1 hover:bg-white/20 rounded disabled:opacity-50"
            data-testid="close-bulk-check-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {!running && !done && (
            <>
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-xs text-cyan-900 space-y-1.5">
                <div className="font-semibold flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" /> Proses akan berjalan SATU PER SATU
                </div>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Setiap ID diberi waktu <b>20 detik</b> untuk merespon.</li>
                  <li>Gagal / timeout akan <b>otomatis dicoba ulang</b> hingga 3 kali.</li>
                  <li>ID dengan tagihan <b>0 (lunas)</b> otomatis diubah statusnya menjadi <b>Lunas</b>.</li>
                  <li>Estimasi: {Math.ceil((targetItems.length * 5) / 60)} - {Math.ceil((targetItems.length * 20) / 60)} menit untuk {targetItems.length} ID.</li>
                </ul>
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="bulk-check-only-pending-label">
                <input
                  type="checkbox"
                  checked={onlyPending}
                  onChange={(e) => setOnlyPending(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="bulk-check-only-pending-checkbox"
                />
                Lewati ID yang statusnya sudah <b>Lunas</b> ({items.length - targetItems.length} ID akan dilewati)
              </label>
            </>
          )}

          {(running || done) && (
            <>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">Progres</span>
                  <span className="font-mono font-bold text-slate-800" data-testid="bulk-check-progress">
                    {processed} / {batchTotal} ({pct}%)
                  </span>
                </div>
                <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-sky-600 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {current && (
                  <div className="text-[11px] text-slate-600 flex items-center gap-1.5" data-testid="bulk-check-current">
                    <Loader2 className="w-3 h-3 animate-spin text-cyan-600" />
                    Mengecek <span className="font-mono font-bold text-slate-800">{current.id}</span>
                    {current.nama && <span className="truncate">— {current.nama}</span>}
                    {current.attempt > 1 && (
                      <span className="text-amber-700">(percobaan {current.attempt}/3)</span>
                    )}
                  </div>
                )}
                <div className="flex gap-3 text-[11px]">
                  <span className="text-teal-700 font-semibold" data-testid="bulk-check-lunas-count">
                    💰 Lunas: {summary.lunas}
                  </span>
                  <span className="text-rose-700 font-semibold" data-testid="bulk-check-unpaid-count">
                    🔴 Masih Menunggak: {summary.unpaid}
                  </span>
                  <span className="text-slate-600 font-semibold" data-testid="bulk-check-error-count">
                    ⚠️ Gagal: {summary.error}
                  </span>
                </div>
                {done && !running && savedAt && (
                  <div className="text-[10.5px] text-slate-500 pt-1 border-t border-slate-200" data-testid="bulk-check-saved-at">
                    💾 Hasil tersimpan otomatis · {new Date(savedAt).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" })}
                  </div>
                )}
              </div>

              <div className="border border-slate-200 rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="bulk-check-results-list">
                {results.length === 0 && (
                  <div className="p-3 text-xs text-slate-500 text-center">Belum ada hasil.</div>
                )}
                {results.map((r) => (
                  <div
                    key={r.id}
                    className="p-2 text-xs flex items-start gap-2"
                    data-testid={`bulk-check-result-${r.id}`}
                  >
                    <span className="mt-0.5">
                      {!r.ok ? "⚠️" : r.lunas ? "💰" : "🔴"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-bold text-slate-800">{r.id}</div>
                      {r.nama && <div className="text-slate-600 truncate">{r.nama}</div>}
                      {r.ok ? (
                        r.lunas ? (
                          <div className="text-teal-700">
                            Lunas (0 tagihan)
                            {r.statusUpdated && <span className="ml-1 text-emerald-700">✓ Status diubah ke Lunas</span>}
                            {r.syncError && <span className="ml-1 text-amber-700">⚠ {r.syncError}</span>}
                          </div>
                        ) : (
                          <div className="text-rose-700">
                            {r.jumlah} tagihan · Rp {Number(r.total_unpaid).toLocaleString("id-ID")}
                          </div>
                        )
                      ) : (
                        <div className="text-rose-700 break-words">Gagal: {r.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t flex gap-2">
          {!running && !done && (
            <>
              <Button variant="outline" className="flex-1" onClick={onClose} data-testid="cancel-bulk-check-btn">
                Batal
              </Button>
              <Button
                className="flex-1 gap-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                onClick={start}
                disabled={targetItems.length === 0}
                data-testid="start-bulk-check-btn"
              >
                <PlayCircle className="w-4 h-4" /> Mulai Cek ({targetItems.length})
              </Button>
            </>
          )}
          {running && (
            <Button
              variant="outline"
              className="flex-1 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={stop}
              data-testid="stop-bulk-check-btn"
            >
              <StopCircle className="w-4 h-4" /> Hentikan
            </Button>
          )}
          {done && !running && (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex gap-2">
                {failedResults.length > 0 && (
                  <Button
                    className="flex-1 gap-1 bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={retryFailed}
                    data-testid="retry-failed-bulk-check-btn"
                  >
                    <RefreshCw className="w-4 h-4" /> Cek Ulang yang Gagal ({failedResults.length})
                  </Button>
                )}
                <Button
                  className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={onClose}
                  data-testid="close-bulk-check-done-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Selesai
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1 text-slate-600 border-slate-300 hover:bg-slate-50 text-xs"
                onClick={clearCache}
                data-testid="clear-bulk-check-cache-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Bersihkan Hasil & Mulai Baru
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
